import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, BACKEND_URL, TOKEN_KEY } from "@/lib/api";
import { Plus, Trash2, X, ArrowDownToLine, Pencil, Boxes, Scale, Plug, Lock, Download, FileSpreadsheet, FileText, Package, Boxes as BoxesIcon, Layers, Printer } from "lucide-react";
import { toast } from "sonner";
import SpecFields, { defaultSpec, formatSpec } from "@/components/SpecFields";
import { useAuth } from "@/context/AuthContext";
import { useBusinessUnit } from "@/context/BusinessUnitContext";
import { useScale } from "@/lib/useScale";
import { triggerBulkLabelPrint } from "@/lib/labelPrint";
import LabelFormatBadge from "@/components/label/LabelFormatBadge";

async function downloadFile(path, filename) {
  const token = localStorage.getItem(TOKEN_KEY);
  const resp = await fetch(`${BACKEND_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  const blob = await resp.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

function DownloadCenter({ products, showRaw = true }) {
  const today = new Date().toISOString().slice(0, 10);
  const [selectedCat, setSelectedCat] = useState(products[0]?.id || "");
  useEffect(() => { if (!selectedCat && products.length) setSelectedCat(products[0].id); }, [products, selectedCat]);
  const selectedProduct = products.find((p) => p.id === selectedCat);
  return (
    <div className="bg-white border border-zinc-300 p-5" data-testid="download-center">
      <div className="flex items-center gap-2 mb-4 border-b border-zinc-200 pb-3">
        <Download size={16} className="text-orange-600" />
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Download Centre</div>
      </div>
      <div className={`grid grid-cols-1 gap-4 md:grid-cols-3 ${showRaw ? "xl:grid-cols-4" : ""}`}>
        <DownloadCard
          testId="rpt-finished"
          title="All Finished Goods"
          subtitle="Every category with carton count split into Available vs Allotted, plus net & gross weight."
          icon={BoxesIcon}
          paths={{
            xlsx: "/api/reports/finished-goods.xlsx", xlsxName: `finished-goods-${today}.xlsx`,
            pdf: "/api/reports/finished-goods.pdf", pdfName: `finished-goods-${today}.pdf`,
          }}
        />
        <DownloadCard
          testId="rpt-available-cartons"
          title="All Available Cartons"
          subtitle="Flat list of every carton currently in stock AND not assigned to any order — across every category."
          icon={Layers}
          paths={{
            xlsx: "/api/reports/available-cartons.xlsx", xlsxName: `available-cartons-${today}.xlsx`,
            pdf: "/api/reports/available-cartons.pdf", pdfName: `available-cartons-${today}.pdf`,
          }}
        />
        {/* Custom label print — direct link (not a download card) */}
        <a
          data-testid="rpt-custom-labels"
          href="/labels/custom"
          className="bg-white border border-orange-300 hover:bg-orange-50 p-4 flex flex-col justify-between transition-colors"
        >
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Printer size={16} className="text-orange-600" />
              <div className="font-semibold text-zinc-950">Custom Label Print</div>
            </div>
            <div className="text-[11px] text-zinc-600 leading-snug">
              Pick cartons from any order, SKU, or paste box numbers directly. Choose which fields to print
              (Box No / Weight / SKU / Spec / Date / Company / Custom) and label size (50×50, 104×50 2-up,
              100×150 packing, or A4 grid).
            </div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700 mt-3">Open →</div>
        </a>
        {showRaw && (
        <DownloadCard
          testId="rpt-raw"
          title="All Raw Material"
          subtitle="Inward inventory: granules, additives, paper tubes, cartons with live stock & weights."
          icon={Package}
          paths={{
            xlsx: "/api/reports/raw-materials.xlsx", xlsxName: `raw-materials-${today}.xlsx`,
            pdf: "/api/reports/raw-materials.pdf", pdfName: `raw-materials-${today}.pdf`,
          }}
        />
        )}
        <div className="bg-white border border-zinc-300 p-5 flex flex-col" data-testid="rpt-category">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Report</div>
              <h3 className="text-base font-semibold mt-1">Item-wise Finished Goods</h3>
              <p className="text-[11px] text-zinc-600 mt-1">Pick a category, get all its cartons with totals below.</p>
            </div>
            <div className="h-9 w-9 bg-zinc-900 text-white flex items-center justify-center"><Layers size={16} /></div>
          </div>
          <select
            data-testid="rpt-cat-select"
            value={selectedCat}
            onChange={(e) => setSelectedCat(e.target.value)}
            className="w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono mb-2"
          >
            {products.length === 0 ? <option value="">-- no categories --</option> : null}
            {products.map((p) => <option key={p.id} value={p.id}>{p.sku}</option>)}
          </select>
          <div className="flex gap-2 mt-auto">
            <button
              data-testid="rpt-cat-xlsx"
              disabled={!selectedCat}
              onClick={() => downloadFile(`/api/reports/category-cartons.xlsx?product_id=${selectedCat}`, `cartons-${selectedProduct?.sku || "category"}.xlsx`)}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs uppercase tracking-wider font-mono disabled:opacity-50"
            ><FileSpreadsheet size={14} /> Excel</button>
            <button
              data-testid="rpt-cat-pdf"
              disabled={!selectedCat}
              onClick={() => downloadFile(`/api/reports/category-cartons.pdf?product_id=${selectedCat}`, `cartons-${selectedProduct?.sku || "category"}.pdf`)}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white text-xs uppercase tracking-wider font-mono disabled:opacity-50"
            ><FileText size={14} /> PDF</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DownloadCard({ title, subtitle, icon: Icon, paths, testId }) {
  return (
    <div className="bg-white border border-zinc-300 p-5 flex flex-col" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Report</div>
          <h3 className="text-base font-semibold mt-1">{title}</h3>
          <p className="text-[11px] text-zinc-600 mt-1 leading-relaxed">{subtitle}</p>
        </div>
        <div className="h-9 w-9 bg-zinc-900 text-white flex items-center justify-center">
          <Icon size={16} />
        </div>
      </div>
      <div className="flex gap-2 mt-auto">
        <button
          data-testid={`${testId}-xlsx`}
          onClick={() => downloadFile(paths.xlsx, paths.xlsxName)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs uppercase tracking-wider font-mono"
        >
          <FileSpreadsheet size={14} /> Excel
        </button>
        <button
          data-testid={`${testId}-pdf`}
          onClick={() => downloadFile(paths.pdf, paths.pdfName)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white text-xs uppercase tracking-wider font-mono"
        >
          <FileText size={14} /> PDF
        </button>
      </div>
    </div>
  );
}

export default function Inventory() {
  const { user } = useAuth();
  const role = user?.role;
  // Sales doesn't see Raw Materials at all
  const canSeeRaw = ["admin", "production"].includes(role);
  const [tab, setTab] = useState("finished");
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Stock</div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-950">Inventory</h1>
        </div>
        {canSeeRaw && (
          <a
            href="/settings"
            data-testid="link-costing-reports"
            className="inline-flex items-center gap-2 border border-orange-500 bg-orange-50 hover:bg-orange-100 text-orange-800 px-4 py-2 text-xs uppercase tracking-wider font-mono"
            title="Stock On Hand · Lots Ledger · Cost Per Carton · Raw Material Usage · Usage Log"
          >
            ₹ Costing Reports →
          </a>
        )}
      </div>

      <DownloadCenter products={products} showRaw={canSeeRaw} />

      <div className="border-b border-zinc-300 flex gap-1">
        {[
          { k: "finished", label: "Finished Goods", show: true },
          { k: "raw", label: "Raw Materials", show: canSeeRaw },
        ].filter((t) => t.show).map((t) => (
          <button
            key={t.k}
            data-testid={`tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-mono border-b-2 -mb-px ${tab === t.k ? "border-orange-600 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "finished" ? <FinishedGoods /> : <RawMaterials />}
    </div>
  );
}

function FinishedGoods() {
  const { user } = useAuth();
  const role = user?.role;
  const canCreateCategory = ["admin", "sales"].includes(role);  // production needs admin approval
  const canEditCategory = role === "admin";                     // edits need admin approval
  const canDeleteCategory = role === "admin";                   // deletes need admin approval
  const canManageCartons = ["admin", "production"].includes(role); // box ops (sales sees only)
  const [items, setItems] = useState([]);
  const [raws, setRaws] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [boxesFor, setBoxesFor] = useState(null);

  const load = async () => {
    const [p, r] = await Promise.all([api.get("/products"), api.get("/raw-materials")]);
    setItems(p.data);
    setRaws(r.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const matName = (id) => {
    const m = raws.find((x) => x.id === id);
    return m ? m.name : "—";
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {canCreateCategory ? (
          <button data-testid="new-sku-btn" onClick={() => { setEditing(null); setShow(true); }} className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm uppercase tracking-wider font-medium flex items-center gap-2"><Plus size={16} /> New Category</button>
        ) : (
          <span data-testid="new-sku-locked" title="Creating a new category requires admin approval" className="inline-flex items-center gap-2 text-sm uppercase tracking-wider font-medium px-4 py-2 border border-dashed border-zinc-300 text-zinc-400 cursor-not-allowed select-none"><Lock size={14} /> New Category</span>
        )}
      </div>
      <div className="bg-white border border-zinc-300 overflow-x-auto" data-testid="products-table">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-zinc-100 text-zinc-600 uppercase text-[10px] font-mono tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Category</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Size</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Color / Grade</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Core</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Carton</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Granule</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300 bg-emerald-50">Available</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300 bg-amber-50">Allotted</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Avail Net (kg)</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Avail Gross (kg)</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {items.length === 0 ? (
              <tr><td colSpan={10} className="text-center text-zinc-500 py-12 text-sm font-sans">No categories yet. Click "New Category" to define your first finished-goods specification.</td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="border-b border-zinc-200 hover:bg-zinc-50 align-top">
                <td className="px-3 py-2 font-semibold text-zinc-900">{p.sku}</td>
                <td className="px-3 py-2 text-xs">{p.spec.thickness_micron}µ · {p.spec.width_mm}mm · {p.spec.weight_per_roll_kg || p.spec.roll_weight_kg}kg/roll × {p.spec.rolls_per_carton || 1} = {(p.spec.roll_weight_kg || 0).toFixed(2)}kg/carton</td>
                <td className="px-3 py-2 font-sans text-xs">{p.spec.color} · {p.spec.grade}</td>
                <td className="px-3 py-2 font-sans text-xs">{p.core_item_id ? matName(p.core_item_id) : <span className="text-zinc-400">—</span>}</td>
                <td className="px-3 py-2 font-sans text-xs">{p.carton_item_id ? matName(p.carton_item_id) : <span className="text-zinc-400">—</span>}</td>
                <td className="px-3 py-2 font-sans text-xs">{p.granule_item_id ? matName(p.granule_item_id) : <span className="text-zinc-400">—</span>}</td>
                <td className="px-3 py-2 text-right text-emerald-700 font-semibold bg-emerald-50/40" data-testid={`available-count-${p.id}`}>
                  {p.available_count ?? p.box_count ?? 0}
                  {p.min_stock_cartons > 0 && (p.available_count ?? p.box_count ?? 0) <= p.min_stock_cartons && (
                    <span className="ml-2 inline-block text-[9px] uppercase border border-orange-400 bg-orange-50 text-orange-800 px-1.5 py-0.5 animate-pulse" data-testid={`fg-low-${p.id}`}>LOW</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right text-amber-700 font-semibold bg-amber-50/40" data-testid={`allotted-count-${p.id}`}>{p.allotted_count || 0}</td>
                <td className="px-3 py-2 text-right">{(p.total_net_kg || 0).toFixed(2)}</td>
                <td className="px-3 py-2 text-right text-orange-700 font-semibold">{(p.total_gross_kg || 0).toFixed(2)}</td>
                {(canEditCategory || canDeleteCategory || canManageCartons || role) && (
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    {canManageCartons && (
                      <button data-testid={`boxes-${p.id}`} onClick={() => setBoxesFor(p)} className="inline-flex items-center gap-1 text-xs uppercase border border-zinc-300 px-2 py-1 hover:bg-zinc-100">
                        <Boxes size={12} /> Cartons
                      </button>
                    )}
                    {canEditCategory ? (
                      <button data-testid={`edit-sku-${p.id}`} onClick={() => { setEditing(p); setShow(true); }} className="text-zinc-600 hover:text-zinc-900"><Pencil size={14} /></button>
                    ) : (
                      <span data-testid={`edit-sku-${p.id}-admin-only`} title="Editing a category requires admin approval" className="text-zinc-300 cursor-not-allowed inline-block"><Lock size={14} /></span>
                    )}
                    {canDeleteCategory ? (
                      <button data-testid={`delete-sku-${p.id}`} onClick={async () => {
                        if (!window.confirm(`Delete category ${p.sku} and all its boxes?`)) return;
                        try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
                      }} className="text-red-600 hover:text-red-800"><Trash2 size={14} /></button>
                    ) : (
                      <span data-testid={`delete-sku-${p.id}-admin-only`} title="Deleting a category requires admin approval" className="text-zinc-300 cursor-not-allowed inline-block"><Lock size={14} /></span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {show && <SkuForm editing={editing} raws={raws} onClose={() => { setShow(false); setEditing(null); }} onSaved={() => { setShow(false); setEditing(null); load(); }} />}
      {boxesFor && <BoxesPanel product={boxesFor} onClose={() => { setBoxesFor(null); load(); }} />}
    </div>
  );
}

function SkuForm({ editing, raws, onClose, onSaved }) {
  const { businessUnit } = useBusinessUnit();
  const isBopp = businessUnit === "bopp_tape";
  const [sku, setSku] = useState(editing?.sku || "");
  const [stock, setStock] = useState(editing?.stock_rolls || 0);
  const [spec, setSpec] = useState(editing?.spec || defaultSpec);
  const [boppSpec, setBoppSpec] = useState(editing?.bopp_spec || {
    base_film_micron: 0, glue_gsm: 0, total_micron: 0,
    colour: "Transparent", width_mm: 0, length_m: 0,
    adhesive_type: "hotmelt", core_id: "3-inch",
  });
  const [coreItemId, setCoreItemId] = useState(editing?.core_item_id || "");
  const [cartonItemId, setCartonItemId] = useState(editing?.carton_item_id || "");
  const [granuleItemId, setGranuleItemId] = useState(editing?.granule_item_id || "");
  const [additiveItemId, setAdditiveItemId] = useState(editing?.additive_item_id || "");
  const [granulePct, setGranulePct] = useState(editing?.granule_pct ?? 99.5);
  const [minStockCartons, setMinStockCartons] = useState(editing?.min_stock_cartons ?? 0);
  const [busy, setBusy] = useState(false);

  // Keep total_micron auto-derived when the user hasn't overridden it.
  // Rule of thumb: total_micron ≈ base_film_micron + (glue_gsm / adhesive_density).
  // Adhesive density is ~1.0 g/cm³ for water-based / hotmelt, so we approximate
  // total ≈ base + glue_gsm.
  useEffect(() => {
    if (!isBopp) return;
    const base = Number(boppSpec.base_film_micron || 0);
    const glue = Number(boppSpec.glue_gsm || 0);
    const derived = base + glue;
    // Only auto-fill if user hasn't manually set total_micron
    if (derived > 0 && (!boppSpec.total_micron || Math.abs(boppSpec.total_micron - (base + glue)) < 0.001)) {
      setBoppSpec((s) => ({ ...s, total_micron: derived }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBopp, boppSpec.base_film_micron, boppSpec.glue_gsm]);

  const cores = raws.filter((r) => r.category === "paper_tube");
  const cartons = raws.filter((r) => r.category === "carton");
  const granules = raws.filter((r) => r.category === "granules");
  const additives = raws.filter((r) => r.category === "master_batch");

  const coreItem = raws.find((r) => r.id === coreItemId);
  const cartonItem = raws.find((r) => r.id === cartonItemId);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const enrichedSpec = {
      ...spec,
      core_type: coreItem?.specification ? `Paper (${coreItem.specification})` : spec.core_type,
      box_type: cartonItem?.specification ? `Carton (${cartonItem.specification})` : spec.box_type,
    };
    const body = {
      sku, spec: enrichedSpec, stock_rolls: stock,
      core_item_id: coreItemId || "",
      carton_item_id: cartonItemId || "",
      granule_item_id: granuleItemId || "",
      additive_item_id: additiveItemId || "",
      granule_pct: granulePct === "" ? 99.5 : Number(granulePct),
      min_stock_cartons: parseInt(minStockCartons) || 0,
      recipe: editing?.recipe || [],
    };
    if (isBopp) {
      body.bopp_spec = {
        base_film_micron: Number(boppSpec.base_film_micron) || 0,
        glue_gsm: Number(boppSpec.glue_gsm) || 0,
        total_micron: Number(boppSpec.total_micron) || 0,
        colour: boppSpec.colour || "",
        width_mm: Number(boppSpec.width_mm) || 0,
        length_m: Number(boppSpec.length_m) || 0,
        adhesive_type: boppSpec.adhesive_type || "hotmelt",
        core_id: boppSpec.core_id || "",
      };
    }
    try {
      if (editing) await api.put(`/products/${editing.id}`, body);
      else await api.post("/products", body);
      toast.success(editing ? "Category updated" : "Category created");
      onSaved();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const matName = (id) => {
    const m = raws.find((x) => x.id === id);
    return m ? `${m.name}${m.specification ? " · " + m.specification : ""} (${m.unit})` : "";
  };

  const cls = "w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-10" data-testid="sku-form-modal">
      <div className="bg-white border border-zinc-300 w-full max-w-4xl">
        <div className="flex items-center justify-between border-b border-zinc-300 px-6 py-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Finished Goods</div>
            <h2 className="text-xl font-semibold">{editing ? `Edit Category · ${editing.sku}` : "New Finished Goods Category"}</h2>
          </div>
          <button onClick={onClose}><X /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className="text-[10px] font-semibold uppercase mb-1 block">Category Name *</label>
            <input data-testid="sku-code" required value={sku} onChange={(e) => setSku(e.target.value)} placeholder="e.g. 23µ-500mm Standard Stretch" className={cls} />
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase mb-1 block flex items-center gap-2">
              <input
                data-testid="sku-min-stock-enabled"
                type="checkbox"
                checked={(parseInt(minStockCartons) || 0) > 0}
                onChange={(e) => setMinStockCartons(e.target.checked ? 1 : 0)}
                className="accent-orange-600"
              />
              Enable Low Stock Alert
            </label>
            <input
              data-testid="sku-min-stock"
              type="number"
              min="0"
              disabled={(parseInt(minStockCartons) || 0) === 0}
              value={minStockCartons}
              onChange={(e) => setMinStockCartons(e.target.value)}
              placeholder="e.g. 5 cartons"
              className={cls + ((parseInt(minStockCartons) || 0) === 0 ? " bg-zinc-100 text-zinc-400" : "")}
            />
            <div className="text-[10px] text-zinc-500 mt-1">When enabled, Dashboard blinks Low Stock when in-stock cartons ≤ this threshold. Uncheck to disable for this SKU.</div>
          </div>

          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2 border-b border-zinc-200 pb-1">Size & Specifications</div>
            <SpecFields spec={spec} setSpec={setSpec} prefix="sku-spec" />
          </div>

          {isBopp && (
            <div className="bg-blue-50/40 border-2 border-dashed border-blue-300 p-4" data-testid="bopp-spec-section">
              <div className="text-[10px] font-mono uppercase tracking-widest text-blue-800 mb-3 flex items-center gap-2">
                <Layers size={11} /> BOPP Jumbo Roll — Additional Specifications
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] font-semibold uppercase mb-1 block">Base Film Micron *</label>
                  <input data-testid="bopp-base-film-micron" type="number" step="0.1" min="0"
                    value={boppSpec.base_film_micron || ""}
                    onChange={(e) => setBoppSpec({ ...boppSpec, base_film_micron: e.target.value })}
                    placeholder="e.g. 28" className={cls} />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase mb-1 block">Glue GSM *</label>
                  <input data-testid="bopp-glue-gsm" type="number" step="0.1" min="0"
                    value={boppSpec.glue_gsm || ""}
                    onChange={(e) => setBoppSpec({ ...boppSpec, glue_gsm: e.target.value })}
                    placeholder="e.g. 22" className={cls} />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase mb-1 block">Total Micron</label>
                  <input data-testid="bopp-total-micron" type="number" step="0.1" min="0"
                    value={boppSpec.total_micron || ""}
                    onChange={(e) => setBoppSpec({ ...boppSpec, total_micron: e.target.value })}
                    placeholder="auto = base + glue" className={cls} />
                  <div className="text-[9px] text-zinc-500 mt-0.5 font-mono">auto-calculated</div>
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase mb-1 block">Colour</label>
                  <select data-testid="bopp-colour"
                    value={boppSpec.colour || "Transparent"}
                    onChange={(e) => setBoppSpec({ ...boppSpec, colour: e.target.value })}
                    className={cls}>
                    {["Transparent", "Brown", "Milky White", "Printed", "Black", "Red", "Blue", "Yellow", "Green"].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase mb-1 block">Width (mm) *</label>
                  <input data-testid="bopp-width" type="number" step="1" min="0"
                    value={boppSpec.width_mm || ""}
                    onChange={(e) => setBoppSpec({ ...boppSpec, width_mm: e.target.value })}
                    placeholder="e.g. 1280" className={cls} />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase mb-1 block">Length (m) *</label>
                  <input data-testid="bopp-length" type="number" step="1" min="0"
                    value={boppSpec.length_m || ""}
                    onChange={(e) => setBoppSpec({ ...boppSpec, length_m: e.target.value })}
                    placeholder="e.g. 4000" className={cls} />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase mb-1 block">Adhesive Type</label>
                  <select data-testid="bopp-adhesive-type"
                    value={boppSpec.adhesive_type || "hotmelt"}
                    onChange={(e) => setBoppSpec({ ...boppSpec, adhesive_type: e.target.value })}
                    className={cls}>
                    <option value="hotmelt">Hot Melt</option>
                    <option value="solvent">Solvent Based</option>
                    <option value="water_based">Water Based</option>
                    <option value="acrylic">Acrylic</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase mb-1 block">Core ID</label>
                  <select data-testid="bopp-core-id"
                    value={boppSpec.core_id || "3-inch"}
                    onChange={(e) => setBoppSpec({ ...boppSpec, core_id: e.target.value })}
                    className={cls}>
                    <option value="3-inch">3-inch (76 mm)</option>
                    <option value="6-inch">6-inch (152 mm)</option>
                    <option value="1-inch">1-inch (25 mm)</option>
                    <option value="2-inch">2-inch (50 mm)</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 border-t border-zinc-200 pt-4">
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block">Core (Paper Tube)</label>
              <select data-testid="sku-core-item" value={coreItemId} onChange={(e) => setCoreItemId(e.target.value)} className={cls}>
                <option value="">-- select paper tube --</option>
                {cores.map((c) => <option key={c.id} value={c.id}>{matName(c.id)}</option>)}
              </select>
              {cores.length === 0 && <div className="text-[10px] text-zinc-500 mt-1">No paper tubes in inward inventory.</div>}
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block">Carton Type</label>
              <select data-testid="sku-carton-item" value={cartonItemId} onChange={(e) => setCartonItemId(e.target.value)} className={cls}>
                <option value="">-- select carton --</option>
                {cartons.map((c) => <option key={c.id} value={c.id}>{matName(c.id)}</option>)}
              </select>
              {cartons.length === 0 && <div className="text-[10px] text-zinc-500 mt-1">No cartons in inward inventory.</div>}
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block">Granule (e.g. LLDPE)</label>
              <select data-testid="sku-granule-item" value={granuleItemId} onChange={(e) => setGranuleItemId(e.target.value)} className={cls}>
                <option value="">-- select granule --</option>
                {granules.map((g) => <option key={g.id} value={g.id}>{matName(g.id)}</option>)}
              </select>
              <div className="mt-2 flex items-center gap-2">
                <label className="text-[10px] font-semibold uppercase whitespace-nowrap">% of Net</label>
                <input
                  data-testid="sku-granule-pct"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={granulePct}
                  onChange={(e) => setGranulePct(e.target.value === "" ? "" : parseFloat(e.target.value))}
                  className={`${cls} text-right`}
                />
              </div>
              {granules.length === 0 && <div className="text-[10px] text-zinc-500 mt-1">No granules in inward inventory.</div>}
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block">Additive (e.g. PIB)</label>
              <select data-testid="sku-additive-item" value={additiveItemId} onChange={(e) => setAdditiveItemId(e.target.value)} className={cls}>
                <option value="">-- select additive --</option>
                {additives.map((g) => <option key={g.id} value={g.id}>{matName(g.id)}</option>)}
              </select>
              <div className="mt-2 flex items-center gap-2">
                <label className="text-[10px] font-semibold uppercase whitespace-nowrap">% of Net</label>
                <input
                  data-testid="sku-additive-pct"
                  type="number"
                  readOnly
                  value={(100 - (granulePct === "" ? 99.5 : Number(granulePct))).toFixed(2)}
                  className={`${cls} bg-orange-50 text-orange-700 font-semibold text-right`}
                />
              </div>
              {additives.length === 0 && <div className="text-[10px] text-zinc-500 mt-1">No additives in inward inventory (add as "Master Batch / Additive" category).</div>}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm uppercase border border-zinc-300 bg-white">Cancel</button>
            <button data-testid="save-sku-btn" type="submit" disabled={busy} className="px-5 py-2 text-sm uppercase bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60">{busy ? "Saving..." : editing ? "Update Category" : "Create Category"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BoxesPanel({ product, onClose }) {
  const [boxes, setBoxes] = useState([]);
  const [adding, setAdding] = useState(false);
  const [quickMode, setQuickMode] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ box_no: "", net_weight_kg: 0, gross_weight_kg: 0, production_date: today, notes: "" });
  const [busy, setBusy] = useState(false);
  const [raws, setRaws] = useState([]);
  const { user } = useAuth();
  const canEdit = ["admin", "production"].includes(user?.role);

  const coreItem = raws.find((r) => r.id === product.core_item_id);
  const cartonItem = raws.find((r) => r.id === product.carton_item_id);
  const rollsPerCarton = product.spec?.rolls_per_carton || 1;
  // Tare = (rolls_per_carton × core unit_weight) + carton unit_weight
  const tare = (rollsPerCarton * (coreItem?.unit_weight_kg || 0)) + (cartonItem?.unit_weight_kg || 0);

  const load = async () => {
    const [b, r] = await Promise.all([
      api.get(`/products/${product.id}/boxes`),
      api.get("/raw-materials"),
    ]);
    setBoxes(b.data);
    setRaws(r.data);
  };
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/products/${product.id}/boxes`, form);
      toast.success("Box added");
      setForm({ box_no: "", net_weight_kg: 0, gross_weight_kg: 0, production_date: today, notes: "" });
      setAdding(false);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const totalNet = boxes.reduce((s, b) => s + (b.net_weight_kg || 0), 0);
  const totalGross = boxes.reduce((s, b) => s + (b.gross_weight_kg || 0), 0);
  const inStock = boxes.filter((b) => b.status === "in_stock").length;
  const assignedCount = boxes.filter((b) => b.status === "in_stock" && b.assigned_order_id).length;
  const availableCount = inStock - assignedCount;
  const [selectedIds, setSelectedIds] = useState({});
  const selectableBoxes = boxes.filter((b) => b.status === "in_stock" && !b.assigned_order_id);
  const selectedCount = Object.keys(selectedIds).filter((id) => selectedIds[id]).length;
  const allSelected = selectableBoxes.length > 0 && selectableBoxes.every((b) => selectedIds[b.id]);
  const toggleAllBoxes = () => {
    if (allSelected) setSelectedIds({});
    else {
      const next = {};
      selectableBoxes.forEach((b) => { next[b.id] = true; });
      setSelectedIds(next);
    }
  };
  const toggleBox = (id) => setSelectedIds((s) => ({ ...s, [id]: !s[id] }));
  const bulkDelete = async () => {
    const ids = Object.keys(selectedIds).filter((id) => selectedIds[id]);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} carton${ids.length === 1 ? "" : "s"}? This will also free any assignment.`)) return;
    const results = await Promise.allSettled(ids.map((bid) => api.delete(`/products/${product.id}/boxes/${bid}`)));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    if (failed === 0) toast.success(`Deleted ${ok} carton${ok === 1 ? "" : "s"}`);
    else if (ok === 0) toast.error(`All ${failed} delete${failed === 1 ? "" : "s"} failed`);
    else toast.warning(`Deleted ${ok}; ${failed} failed — see console`);
    setSelectedIds({});
    load();
  };

  const cls = "w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono";

  if (quickMode) {
    return (
      <QuickWeigh
        product={product}
        boxes={boxes}
        tare={tare}
        coreItem={coreItem}
        cartonItem={cartonItem}
        onAdded={load}
        onClose={() => setQuickMode(false)}
        onExit={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-10" data-testid="boxes-panel">
      <div className="bg-white border border-zinc-300 w-full max-w-4xl">
        <div className="flex items-center justify-between border-b border-zinc-300 px-6 py-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Box Register</div>
            <h2 className="text-xl font-semibold">{product.sku}</h2>
            <div className="text-xs text-zinc-600 mt-1 font-mono">{product.spec.thickness_micron}µ · {product.spec.width_mm}mm · {rollsPerCarton} rolls/carton · tare {tare.toFixed(3)}kg ({rollsPerCarton}×core + carton)</div>
          </div>
          <div className="flex items-center gap-2">
            <LabelFormatBadge />
            {canEdit && (
              <button
                data-testid="quick-weigh-btn"
                onClick={() => setQuickMode(true)}
                className="bg-zinc-900 hover:bg-zinc-800 text-white px-4 py-2 text-xs uppercase tracking-wider font-mono flex items-center gap-2"
              >
                <Scale size={14} /> Quick Weigh
              </button>
            )}
            <button onClick={onClose}><X /></button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-zinc-300">
          <div className="p-4 border-r border-zinc-300">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Cartons in stock</div>
            <div className="font-mono text-2xl text-zinc-900" data-testid="total-boxes">{inStock}</div>
            <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
              <span className="text-emerald-700 font-semibold" data-testid="boxes-available">{availableCount} available</span>
              {" · "}
              <span className="text-orange-700 font-semibold" data-testid="boxes-assigned">{assignedCount} assigned</span>
            </div>
          </div>
          <div className="p-4 border-r border-zinc-300">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Total Net (kg)</div>
            <div className="font-mono text-2xl text-zinc-900" data-testid="total-net">{totalNet.toFixed(2)}</div>
          </div>
          <div className="p-4 border-r border-zinc-300">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Total Gross (kg)</div>
            <div className="font-mono text-2xl text-orange-700 font-semibold" data-testid="total-gross">{totalGross.toFixed(2)}</div>
          </div>
          <div className="p-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Reserved (net kg)</div>
            <div className="font-mono text-2xl text-orange-700 font-semibold" data-testid="reserved-net">{boxes.filter(b => b.status === "in_stock" && b.assigned_order_id).reduce((s, b) => s + (b.net_weight_kg || 0), 0).toFixed(2)}</div>
          </div>
        </div>

        {canEdit && (
          <div className="p-4 border-b border-zinc-300 bg-zinc-50">
            {!adding ? (
              <button data-testid="add-box-btn" onClick={() => setAdding(true)} className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm uppercase tracking-wider font-medium flex items-center gap-2"><Plus size={14} /> Add Carton (record actual weight)</button>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
                <div>
                  <label className="text-[10px] font-semibold uppercase mb-1 block">Box No.</label>
                  <input data-testid="box-no" value={form.box_no} onChange={(e) => setForm({ ...form, box_no: e.target.value })} placeholder="auto" className={cls} />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase mb-1 block">Gross Wt (kg) *</label>
                  <input
                    data-testid="box-gross"
                    type="number"
                    step="0.001"
                    required
                    value={form.gross_weight_kg}
                    onChange={(e) => {
                      const g = parseFloat(e.target.value) || 0;
                      const n = Math.max(0, +(g - tare).toFixed(3));
                      setForm({ ...form, gross_weight_kg: g, net_weight_kg: n });
                    }}
                    className={cls}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase mb-1 block">Net Wt (auto, = granule) <span className="text-zinc-500 normal-case">−{tare.toFixed(3)} kg tare ({rollsPerCarton}×core + carton)</span></label>
                  <input
                    data-testid="box-net"
                    type="number"
                    step="0.001"
                    value={form.net_weight_kg}
                    readOnly
                    className={`${cls} bg-orange-50 text-orange-700 font-semibold`}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase mb-1 block">Prod. Date</label>
                  <input data-testid="box-date" type="date" value={form.production_date} onChange={(e) => setForm({ ...form, production_date: e.target.value })} className={cls} />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase mb-1 block">Notes</label>
                  <input data-testid="box-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={cls} />
                </div>
                <div className="flex gap-1">
                  <button data-testid="save-box-btn" type="submit" disabled={busy} className="flex-1 h-9 px-3 text-xs uppercase bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60">{busy ? "..." : "Save"}</button>
                  <button type="button" onClick={() => setAdding(false)} className="h-9 px-3 text-xs uppercase border border-zinc-300 bg-white">×</button>
                </div>
              </form>
            )}
          </div>
        )}

        {canEdit && selectedCount > 0 && (
          <div className="border-b border-orange-300 bg-orange-50 px-4 py-2 flex items-center justify-between gap-2 flex-wrap" data-testid="bulk-action-bar">
            <div className="text-xs font-mono uppercase tracking-wider text-orange-800">{selectedCount} selected</div>
            <div className="flex gap-2 items-center flex-wrap">
              <LabelFormatBadge />
              <button
                data-testid="bulk-print-labels-btn"
                onClick={() => {
                  const ids = Object.keys(selectedIds).filter((id) => selectedIds[id]);
                  const idSet = new Set(ids);
                  const boxNos = boxes.filter((b) => idSet.has(b.id)).map((b) => b.box_no).filter(Boolean);
                  if (boxNos.length === 0) { toast.error("No carton numbers to print"); return; }
                  triggerBulkLabelPrint(boxNos);
                  toast.success(`Sent ${boxNos.length} label${boxNos.length === 1 ? "" : "s"} to printer`);
                }}
                className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-orange-500 bg-white text-orange-700 hover:bg-orange-50 px-3 py-1.5"
              ><Printer size={12} /> Print labels</button>
              <button
                data-testid="bulk-delete-btn"
                onClick={bulkDelete}
                className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-red-500 bg-white text-red-700 hover:bg-red-50 px-3 py-1.5"
              ><Trash2 size={12} /> Delete selected</button>
              <button
                data-testid="bulk-clear-btn"
                onClick={() => setSelectedIds({})}
                className="text-[11px] uppercase font-mono border border-zinc-300 px-3 py-1.5 hover:bg-zinc-100"
              >Clear</button>
            </div>
          </div>
        )}

        <div className="max-h-96 overflow-y-auto" data-testid="boxes-table">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-zinc-100 text-zinc-600 uppercase text-[10px] font-mono tracking-wider sticky top-0">
              <tr>
                {canEdit && (
                  <th className="text-left px-3 py-2 border-b border-zinc-300 w-8">
                    <input
                      data-testid="boxes-select-all"
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAllBoxes}
                      className="accent-orange-600 cursor-pointer"
                      title="Select all in-stock unassigned cartons"
                    />
                  </th>
                )}
                <th className="text-left px-3 py-2 border-b border-zinc-300">Box No</th>
                <th className="text-left px-3 py-2 border-b border-zinc-300">Date</th>
                <th className="text-right px-3 py-2 border-b border-zinc-300">Net (kg)</th>
                <th className="text-right px-3 py-2 border-b border-zinc-300">Gross (kg)</th>
                <th className="text-left px-3 py-2 border-b border-zinc-300">Status</th>
                <th className="text-left px-3 py-2 border-b border-zinc-300">By</th>
                <th className="text-left px-3 py-2 border-b border-zinc-300">Notes</th>
                <th className="border-b border-zinc-300"></th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {boxes.length === 0 ? (
                <tr><td colSpan={9} className="text-center text-zinc-500 py-8 text-sm font-sans">No cartons recorded yet for this category.</td></tr>
              ) : boxes.map((b) => (
                <tr key={b.id} className={`border-b border-zinc-200 hover:bg-zinc-50 ${b.assigned_order_no ? "bg-orange-50/50" : ""} ${selectedIds[b.id] ? "bg-orange-100" : ""}`} data-testid={`box-row-${b.id}`}>
                  {canEdit && (
                    <td className="px-3 py-2 align-top">
                      {b.status === "in_stock" && !b.assigned_order_id ? (
                        <input
                          data-testid={`box-select-${b.id}`}
                          type="checkbox"
                          checked={!!selectedIds[b.id]}
                          onChange={() => toggleBox(b.id)}
                          className="accent-orange-600"
                        />
                      ) : (
                        <span className="text-zinc-300"><Lock size={12} /></span>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2 font-semibold">
                    <div>{b.box_no}</div>
                    {b.assigned_order_no && (
                      <Link
                        to="/orders"
                        data-testid={`box-assigned-${b.id}`}
                        title={`Reserved for order ${b.assigned_order_no} — cannot be assigned elsewhere`}
                        className="mt-1 inline-flex items-center gap-1 text-[9px] uppercase font-mono border border-orange-400 bg-orange-100 text-orange-800 px-1.5 py-0.5"
                      >
                        <Lock size={9} /> {b.assigned_order_no}
                      </Link>
                    )}
                  </td>
                  <td className="px-3 py-2">{b.production_date}</td>
                  <td className="px-3 py-2 text-right">{b.net_weight_kg.toFixed(3)}</td>
                  <td className="px-3 py-2 text-right text-orange-700 font-semibold">{b.gross_weight_kg.toFixed(3)}</td>
                  <td className="px-3 py-2"><span className="text-[10px] uppercase border border-zinc-300 bg-zinc-100 text-zinc-700 px-2 py-0.5">{b.location || "Kani"}</span></td>
                  <td className="px-3 py-2"><span className="text-[10px] uppercase border border-zinc-300 bg-zinc-100 text-zinc-700 px-2 py-0.5">{b.status}</span></td>
                  <td className="px-3 py-2 text-[10px]">{b.created_by}</td>
                  <td className="px-3 py-2 text-xs font-sans">{b.notes || "—"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <a
                      data-testid={`print-label-${b.id}`}
                      href={`/label/${b.box_no}?autoprint=1`}
                      target="_blank"
                      rel="noreferrer"
                      title={`Print label for ${b.box_no}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-orange-300 bg-orange-50 text-orange-700 px-2 py-1 hover:bg-orange-100 mr-1"
                    ><Printer size={10} /> Label</a>
                    {canEdit && (
                      <button
                        data-testid={`rename-box-${b.id}`}
                        title="Rename carton number"
                        onClick={async () => {
                          const nb = window.prompt(`Rename carton number\n\nCurrent: ${b.box_no}\nEnter new value (any prefix / suffix accepted).`, b.box_no);
                          if (!nb || nb.trim() === b.box_no) return;
                          try {
                            await api.patch(`/products/${product.id}/boxes/${b.id}/rename`, { box_no: nb.trim() });
                            toast.success(`Renamed to ${nb.trim()}`);
                            load();
                          } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
                        }}
                        className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-zinc-300 text-zinc-700 px-2 py-1 hover:bg-zinc-100 mr-1"
                      >Rename</button>
                    )}
                    {canEdit && (
                      <button data-testid={`delete-box-${b.id}`} onClick={async () => {
                        if (!window.confirm(`Delete box ${b.box_no}?`)) return;
                        try { await api.delete(`/products/${product.id}/boxes/${b.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
                      }} className="text-red-600 hover:text-red-800 inline-block align-middle"><Trash2 size={12} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function QuickWeigh({ product, boxes, tare, coreItem, cartonItem, onAdded, onClose, onExit }) {
  const { businessUnit } = useBusinessUnit();
  const isBopp = businessUnit === "bopp_tape";
  // For BOPP, no cores/carton wrappers → no tare, no target-per-roll weight.
  const effectiveTare = isBopp ? 0 : tare;
  const [mode, setMode] = useState(isBopp ? "net" : "gross"); // 'gross' | 'net'
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [autoSave, setAutoSave] = useState(!isBopp); // BOPP always manual entry
  const [printMode, setPrintMode] = useState("auto"); // 'auto' | 'manual'
  const [baudRate, setBaudRate] = useState(9600);
  const [tolerancePct, setTolerancePct] = useState(5);
  const [prodDate, setProdDate] = useState(new Date().toISOString().slice(0, 10));
  const inputRef = React.useRef(null);
  const printFrameRef = React.useRef(null);

  // BOPP-only extra capture state — pre-filled from the product's saved
  // BOPP spec so the operator only needs to tweak per-roll deltas.
  const [boppGross, setBoppGross] = useState("");
  const [boppRoll, setBoppRoll] = useState(() => ({
    width_mm: product?.bopp_spec?.width_mm || "",
    length_m: product?.bopp_spec?.length_m || "",
    base_film_micron: product?.bopp_spec?.base_film_micron || "",
    glue_gsm: product?.bopp_spec?.glue_gsm || "",
    total_micron: product?.bopp_spec?.total_micron || "",
    colour: product?.bopp_spec?.colour || "Transparent",
    core_id: product?.bopp_spec?.core_id || "3-inch",
  }));

  // Trigger a label print for a freshly-saved carton via a hidden iframe.
  // The CartonLabel page reads ?autoprint=1 and calls window.print() once
  // its data is loaded; the browser shows the native print dialog so the
  // operator can pick the label printer the first time (remembered after).
  const triggerLabelPrint = useCallback((boxNo) => {
    if (!boxNo) return;
    let frame = printFrameRef.current;
    if (!frame) {
      frame = document.createElement("iframe");
      frame.setAttribute("data-testid", "label-print-frame");
      frame.style.position = "fixed";
      frame.style.right = "0";
      frame.style.bottom = "0";
      frame.style.width = "0";
      frame.style.height = "0";
      frame.style.border = "0";
      frame.style.visibility = "hidden";
      document.body.appendChild(frame);
      printFrameRef.current = frame;
    }
    frame.src = `/label/${boxNo}?autoprint=1`;
  }, []);

  React.useEffect(() => {
    return () => {
      if (printFrameRef.current) {
        try { document.body.removeChild(printFrameRef.current); } catch {}
        printFrameRef.current = null;
      }
    };
  }, []);

  const scale = useScale({ baudRate, stableSamples: 3 });
  const lastSavedRef = React.useRef(0);

  const targetPerRoll = product.spec.weight_per_roll_kg || product.spec.roll_weight_kg / Math.max(1, product.spec.rolls_per_carton || 1);
  const rollsPerCarton = product.spec.rolls_per_carton || 1;

  // Compute deviation for the current input value
  const w = parseFloat(value) || 0;
  let netNow = 0;
  if (w > 0) {
    netNow = mode === "gross" ? Math.max(0, w - effectiveTare) : w;
  }
  const perRoll = netNow > 0 ? netNow / rollsPerCarton : 0;
  const deviationPct = (perRoll && targetPerRoll) ? ((perRoll - targetPerRoll) / targetPerRoll) * 100 : 0;
  // BOPP jumbo rolls don't have a target-per-roll spec → skip tolerance guard.
  const outOfTolerance = !isBopp && w > 0 && Math.abs(deviationPct) > tolerancePct;

  React.useEffect(() => {
    inputRef.current?.focus();
  }, [busy]);

  // Mirror live scale reading into the input
  React.useEffect(() => {
    if (scale.connected && scale.reading != null) {
      setValue(scale.reading.toFixed(3));
    }
  }, [scale.reading, scale.connected]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const todayCount = boxes.filter((b) => b.production_date === todayStr).length;
  const todayNet = boxes.filter((b) => b.production_date === todayStr).reduce((s, b) => s + (b.net_weight_kg || 0), 0);
  const todayGross = boxes.filter((b) => b.production_date === todayStr).reduce((s, b) => s + (b.gross_weight_kg || 0), 0);

  const persistBox = useCallback(async (rawWeight) => {
    if (!rawWeight || rawWeight <= 0) return;
    setBusy(true);
    let net, gross;
    if (isBopp) {
      // Jumbo roll: user's main input is Net; Gross is a separate manual entry.
      // If the operator didn't supply a distinct gross, we fall back to net so
      // the record still saves (they can edit later from the carton row).
      net = mode === "gross" ? Math.max(0, +(rawWeight - effectiveTare).toFixed(3)) : rawWeight;
      const g = parseFloat(boppGross);
      gross = g > 0 ? g : net;
    } else if (mode === "gross") {
      gross = rawWeight;
      net = Math.max(0, +(rawWeight - effectiveTare).toFixed(3));
    } else {
      net = rawWeight;
      gross = +(rawWeight + effectiveTare).toFixed(3);
    }
    try {
      const body = {
        net_weight_kg: net,
        gross_weight_kg: gross,
        production_date: todayStr,
      };
      if (isBopp) {
        body.jumbo_roll_spec = {
          width_mm: Number(boppRoll.width_mm) || 0,
          length_m: Number(boppRoll.length_m) || 0,
          base_film_micron: Number(boppRoll.base_film_micron) || 0,
          glue_gsm: Number(boppRoll.glue_gsm) || 0,
          total_micron: Number(boppRoll.total_micron) || 0,
          colour: boppRoll.colour || "",
          core_id: boppRoll.core_id || "",
        };
      }
      const { data } = await api.post(`/products/${product.id}/boxes`, body);
      setRecent((r) => [data, ...r].slice(0, 8));
      setValue("");
      if (isBopp) setBoppGross("");
      onAdded();
      if (printMode === "auto") triggerLabelPrint(data.box_no);
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.connect(g); g.connect(ctx.destination);
        osc.frequency.value = 880;
        g.gain.setValueAtTime(0.1, ctx.currentTime);
        osc.start();
        osc.stop(ctx.currentTime + 0.08);
      } catch {}
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [mode, effectiveTare, isBopp, boppGross, boppRoll, product.id, todayStr, onAdded, printMode, triggerLabelPrint]);

  // Auto-save when scale signals a stable reading
  React.useEffect(() => {
    scale.setOnStable(async (v) => {
      if (!autoSave || isBopp) return;
      // Skip auto-save if out of tolerance — force manual review
      const n = mode === "gross" ? Math.max(0, v - effectiveTare) : v;
      const pr = n / rollsPerCarton;
      if (pr > 0 && Math.abs((pr - targetPerRoll) / targetPerRoll) * 100 > tolerancePct) return;
      const now = Date.now();
      if (now - lastSavedRef.current < 1500) return;
      if (!v || v <= 0) return;
      lastSavedRef.current = now;
      await persistBox(v);
    });
  }, [autoSave, persistBox, scale, mode, effectiveTare, rollsPerCarton, targetPerRoll, tolerancePct, isBopp]);

  const submitOne = async (e) => {
    e?.preventDefault();
    const w = parseFloat(value);
    if (!w || w <= 0) return;
    await persistBox(w);
  };

  const undo = async () => {
    const last = recent[0];
    if (!last) return;
    try {
      await api.delete(`/products/${product.id}/boxes/${last.id}`);
      setRecent(recent.slice(1));
      onAdded();
      toast.success(`Removed ${last.box_no}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-950 z-50 flex flex-col" data-testid="quick-weigh" style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800 px-3 sm:px-6 py-2 sm:py-3 flex flex-wrap items-center justify-between gap-2 text-white">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="h-8 w-8 bg-orange-600 flex items-center justify-center shrink-0">
            <Scale size={16} className="text-white" />
          </div>
          <div className="min-w-0">
            <div className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-orange-500">Quick Weigh Mode</div>
            <div className="font-semibold text-sm truncate">{product.sku}</div>
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2 justify-end">
          {/* Scale connect button */}
          {scale.supported ? (
            scale.connected ? (
              <div className="flex items-center gap-2 border border-orange-600 bg-orange-600/10 px-3 py-1.5">
                <span className="h-2 w-2 bg-orange-500 rounded-full animate-pulse"></span>
                <span className="text-[10px] font-mono uppercase tracking-widest text-orange-400">Scale Live · {baudRate} bd</span>
                <button data-testid="scale-disconnect" onClick={scale.disconnect} className="text-[10px] font-mono uppercase text-zinc-400 hover:text-white ml-2">Disconnect</button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <select
                  data-testid="scale-baud"
                  value={baudRate}
                  onChange={(e) => setBaudRate(parseInt(e.target.value))}
                  className="h-9 px-2 text-xs bg-zinc-800 border border-zinc-700 text-zinc-200 font-mono"
                >
                  {[1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200].map((b) => (
                    <option key={b} value={b}>{b} bd</option>
                  ))}
                </select>
                <button
                  data-testid="scale-connect"
                  onClick={scale.connect}
                  className="border border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-3 py-1.5 text-xs uppercase tracking-wider font-mono flex items-center gap-2"
                >
                  <Plug size={12} /> Connect Scale
                </button>
              </div>
            )
          ) : (
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border border-zinc-800 px-3 py-1.5">Browser doesn't support Web Serial</div>
          )}

          <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400 ml-2">
            <input
              data-testid="auto-save-toggle"
              type="checkbox"
              checked={autoSave}
              onChange={(e) => setAutoSave(e.target.checked)}
              className="accent-orange-600"
            />
            Auto-save on stable
          </label>

          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400 ml-2 border border-zinc-700 px-2 py-1">
            <span className="text-zinc-500">Print</span>
            <button
              data-testid="print-mode-auto"
              type="button"
              onClick={() => setPrintMode("auto")}
              className={`px-2 py-0.5 ${printMode === "auto" ? "bg-orange-600 text-white" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}
            >Auto</button>
            <button
              data-testid="print-mode-manual"
              type="button"
              onClick={() => setPrintMode("manual")}
              className={`px-2 py-0.5 ${printMode === "manual" ? "bg-orange-600 text-white" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}
            >Manual</button>
          </div>

          <div className="flex items-center gap-2 ml-2 border border-zinc-700 px-2 py-1">
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Date</label>
            <input
              data-testid="quick-date"
              type="date"
              value={prodDate}
              onChange={(e) => setProdDate(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 text-zinc-200 font-mono text-xs px-2 py-0.5"
            />
          </div>

          <button data-testid="quick-exit-list" onClick={onClose} className="text-xs uppercase font-mono px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 ml-2">View List</button>
          <button data-testid="quick-exit-close" onClick={onExit} className="text-xs uppercase font-mono px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 flex items-center gap-1"><X size={12} /> Exit</button>
        </div>
      </div>

      {scale.error && (
        <div className="bg-zinc-900 border-b border-zinc-800 px-6 py-2 text-[11px] font-mono text-orange-400" data-testid="scale-error">
          Scale: {scale.error}
        </div>
      )}

      {/* Live stats strip */}
      <div className="bg-zinc-900 border-b border-zinc-800 grid grid-cols-2 md:grid-cols-5 text-white">
        <Stat label="Today Cartons" value={todayCount} />
        <Stat label="Today Net (kg)" value={todayNet.toFixed(2)} />
        <Stat label="Today Gross (kg)" value={todayGross.toFixed(2)} color="text-orange-500" />
        <Stat label="Tare (core+carton)" value={tare.toFixed(3) + " kg"} />
        <Stat label="Target / Carton" value={(product.spec.roll_weight_kg + tare).toFixed(3) + " kg"} />
      </div>

      {/* Main input area */}
      <div className="flex-1 flex items-center justify-center px-3 sm:px-6 py-4 sm:py-8 bg-zinc-950 overflow-y-auto">
        <form onSubmit={submitOne} className="w-full max-w-2xl text-center">
          <div className="flex gap-1 justify-center mb-4 sm:mb-6 flex-wrap">
            {["gross", "net"].map((m) => (
              <button
                type="button"
                key={m}
                data-testid={`quick-mode-${m}`}
                onClick={() => setMode(m)}
                className={`px-3 sm:px-4 py-1.5 text-[11px] uppercase font-mono tracking-wider border ${mode === m ? "bg-orange-600 text-white border-orange-600" : "bg-transparent text-zinc-400 border-zinc-700 hover:text-white"}`}
              >Enter {m.toUpperCase()} Weight</button>
            ))}
          </div>

          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3 px-2">
            {scale.connected
              ? (scale.stable ? "● STABLE READING — saving…" : "○ live reading from scale")
              : (mode === "gross" ? "Read directly from scale" : "Enter net (film) weight")}
          </div>

          <div className="relative">
            <input
              ref={inputRef}
              data-testid="quick-weight-input"
              type="number"
              step="0.001"
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="0.000"
              disabled={busy}
              readOnly={scale.connected}
              className={`w-full text-center bg-zinc-900 border-2 ${outOfTolerance ? "border-orange-500 ring-2 ring-orange-500 animate-pulse" : scale.connected ? (scale.stable ? "border-orange-500" : "border-zinc-600") : "border-zinc-700 focus:border-orange-600"} text-white font-mono font-bold py-4 sm:py-8 outline-none disabled:opacity-50 ${scale.stable && !outOfTolerance ? "ring-2 ring-orange-600" : ""}`}
              style={{ fontSize: "clamp(3.5rem, 18vw, 8rem)", lineHeight: 1 }}
            />
            <div className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 text-orange-500 font-mono text-lg sm:text-2xl uppercase tracking-widest">kg</div>
          </div>

          {isBopp && (
            <div className="mt-4 bg-zinc-900 border-2 border-dashed border-blue-500/60 p-3" data-testid="bopp-quick-weigh-fields">
              <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-2 text-center">
                Jumbo Roll Details · overrides product defaults
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-white">
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                  Gross (kg)
                  <input
                    data-testid="bopp-gross-input"
                    type="number" step="0.001" min="0"
                    value={boppGross}
                    onChange={(e) => setBoppGross(e.target.value)}
                    placeholder="optional"
                    className="mt-1 w-full bg-zinc-950 border border-zinc-700 text-white font-mono px-2 py-1.5 text-sm focus:border-blue-500 outline-none"
                  />
                </label>
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                  Width (mm)
                  <input
                    data-testid="bopp-roll-width"
                    type="number" step="1" min="0"
                    value={boppRoll.width_mm}
                    onChange={(e) => setBoppRoll({ ...boppRoll, width_mm: e.target.value })}
                    className="mt-1 w-full bg-zinc-950 border border-zinc-700 text-white font-mono px-2 py-1.5 text-sm focus:border-blue-500 outline-none"
                  />
                </label>
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                  Length (m)
                  <input
                    data-testid="bopp-roll-length"
                    type="number" step="1" min="0"
                    value={boppRoll.length_m}
                    onChange={(e) => setBoppRoll({ ...boppRoll, length_m: e.target.value })}
                    className="mt-1 w-full bg-zinc-950 border border-zinc-700 text-white font-mono px-2 py-1.5 text-sm focus:border-blue-500 outline-none"
                  />
                </label>
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                  Base Micron
                  <input
                    data-testid="bopp-roll-base-micron"
                    type="number" step="0.1" min="0"
                    value={boppRoll.base_film_micron}
                    onChange={(e) => setBoppRoll({ ...boppRoll, base_film_micron: e.target.value })}
                    className="mt-1 w-full bg-zinc-950 border border-zinc-700 text-white font-mono px-2 py-1.5 text-sm focus:border-blue-500 outline-none"
                  />
                </label>
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                  Glue GSM
                  <input
                    data-testid="bopp-roll-glue-gsm"
                    type="number" step="0.1" min="0"
                    value={boppRoll.glue_gsm}
                    onChange={(e) => setBoppRoll({ ...boppRoll, glue_gsm: e.target.value })}
                    className="mt-1 w-full bg-zinc-950 border border-zinc-700 text-white font-mono px-2 py-1.5 text-sm focus:border-blue-500 outline-none"
                  />
                </label>
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                  Total Micron
                  <input
                    data-testid="bopp-roll-total-micron"
                    type="number" step="0.1" min="0"
                    value={boppRoll.total_micron}
                    onChange={(e) => setBoppRoll({ ...boppRoll, total_micron: e.target.value })}
                    className="mt-1 w-full bg-zinc-950 border border-zinc-700 text-white font-mono px-2 py-1.5 text-sm focus:border-blue-500 outline-none"
                  />
                </label>
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                  Colour
                  <select
                    data-testid="bopp-roll-colour"
                    value={boppRoll.colour}
                    onChange={(e) => setBoppRoll({ ...boppRoll, colour: e.target.value })}
                    className="mt-1 w-full bg-zinc-950 border border-zinc-700 text-white font-mono px-2 py-1.5 text-sm focus:border-blue-500 outline-none"
                  >
                    {["Transparent", "Brown", "Milky White", "Printed", "Black", "Red", "Blue", "Yellow", "Green"].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </label>
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                  Core ID
                  <select
                    data-testid="bopp-roll-core"
                    value={boppRoll.core_id}
                    onChange={(e) => setBoppRoll({ ...boppRoll, core_id: e.target.value })}
                    className="mt-1 w-full bg-zinc-950 border border-zinc-700 text-white font-mono px-2 py-1.5 text-sm focus:border-blue-500 outline-none"
                  >
                    <option value="3-inch">3-inch (76 mm)</option>
                    <option value="6-inch">6-inch (152 mm)</option>
                    <option value="1-inch">1-inch (25 mm)</option>
                    <option value="2-inch">2-inch (50 mm)</option>
                  </select>
                </label>
              </div>
              <div className="text-[9px] font-mono text-zinc-500 mt-2 text-center">
                Main weight input above = <b className="text-white">Net</b>. Gross falls back to Net if left blank.
              </div>
            </div>
          )}

          {/* Tolerance bar — hidden for BOPP (no target-per-roll spec) */}
          {!isBopp && (
          <div className="mt-3 flex items-center justify-center gap-3">
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Tolerance ±</label>
            <input
              data-testid="tolerance-pct"
              type="number"
              min="0"
              step="0.5"
              value={tolerancePct}
              onChange={(e) => setTolerancePct(parseFloat(e.target.value) || 0)}
              className="w-16 text-center bg-zinc-900 border border-zinc-700 text-white font-mono py-1 text-xs"
            />
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">% per roll · target {targetPerRoll.toFixed(3)} kg × {rollsPerCarton} roll{rollsPerCarton > 1 ? "s" : ""}</span>
          </div>
          )}

          {outOfTolerance && (
            <div data-testid="tolerance-alert" className="mt-3 inline-block border-2 border-orange-500 bg-orange-500/10 px-4 py-2 font-mono text-orange-400 uppercase tracking-widest text-xs">
              ⚠ Out of tolerance · per-roll {perRoll.toFixed(3)} kg ({deviationPct > 0 ? "+" : ""}{deviationPct.toFixed(1)}%) · re-check before saving
            </div>
          )}

          <div className="mt-4 flex gap-2 justify-center flex-wrap">
            <button
              data-testid="quick-save-btn"
              type="submit"
              disabled={busy || !value}
              className="bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs sm:text-sm px-5 sm:px-8 py-2.5 sm:py-3 disabled:opacity-40 active:scale-95"
            >
              {busy ? "Saving..." : scale.connected && autoSave ? "Save Now [↵]" : "Save & Next [↵ Enter]"}
            </button>
            <button
              data-testid="quick-undo-btn"
              type="button"
              onClick={undo}
              disabled={recent.length === 0}
              className="border border-zinc-700 text-zinc-300 hover:bg-zinc-800 font-mono uppercase tracking-widest text-xs sm:text-sm px-4 sm:px-6 py-2.5 sm:py-3 disabled:opacity-30"
            >
              Undo Last
            </button>
          </div>

          {value && (
            <div className="mt-6 inline-grid grid-cols-2 gap-x-8 gap-y-1 text-left font-mono text-sm">
              <div className="text-zinc-500 uppercase text-[10px] tracking-widest">Computed Net</div>
              <div className="text-white">{mode === "gross" ? Math.max(0, parseFloat(value) - tare).toFixed(3) : parseFloat(value).toFixed(3)} kg</div>
              <div className="text-zinc-500 uppercase text-[10px] tracking-widest">Computed Gross</div>
              <div className="text-orange-500">{mode === "gross" ? parseFloat(value).toFixed(3) : (parseFloat(value) + tare).toFixed(3)} kg</div>
            </div>
          )}
        </form>
      </div>

      {/* Recent strip */}
      <div className="bg-zinc-900 border-t border-zinc-800 px-3 sm:px-6 py-3" data-testid="quick-recent">
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Recent cartons this session</div>
          <div className="flex items-center gap-2">
            <LabelFormatBadge dark />
            {recent.length > 1 && (
              <button
                data-testid="recent-print-all-btn"
                onClick={() => {
                  const boxNos = recent.map((b) => b.box_no).filter(Boolean);
                  if (boxNos.length === 0) return;
                  triggerBulkLabelPrint(boxNos);
                }}
                className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-orange-600 bg-orange-600/10 text-orange-400 hover:bg-orange-600/20 px-2 py-1"
                title="Print labels for all cartons recorded in this Quick Weigh session"
              ><Printer size={10} /> Print all {recent.length}</button>
            )}
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto">
          {recent.length === 0 ? (
            <div className="text-xs text-zinc-600 font-mono">— No cartons recorded yet —</div>
          ) : recent.map((b) => (
            <div key={b.id} className="border border-zinc-800 bg-zinc-950 px-3 py-2 min-w-[140px] flex flex-col gap-1">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{b.box_no}</div>
                <button
                  data-testid={`recent-print-${b.box_no}`}
                  onClick={() => triggerLabelPrint(b.box_no)}
                  title="Print label"
                  className="text-zinc-400 hover:text-orange-400"
                ><Printer size={11} /></button>
              </div>
              <div className="font-mono text-white text-sm">{b.net_weight_kg.toFixed(2)} / <span className="text-orange-500">{b.gross_weight_kg.toFixed(2)}</span></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color = "text-white" }) {
  return (
    <div className="px-6 py-3 border-r border-zinc-800 last:border-r-0">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono text-xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function RawMaterials() {
  const { user } = useAuth();
  const role = user?.role;
  const canCreate = ["admin", "production"].includes(role);     // production can add inward
  const canReceive = ["admin", "production"].includes(role);    // production can receive stock
  const canEditRaw = role === "admin";                          // edits need admin approval
  const canDeleteRaw = role === "admin";                        // deletes need admin approval
  const showActions = canReceive || canEditRaw || canDeleteRaw;
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [receive, setReceive] = useState(null);
  const [filterCat, setFilterCat] = useState("");

  const load = async () => { const r = await api.get("/raw-materials"); setItems(r.data); };
  useEffect(() => { load().catch(() => {}); }, []);

  const visible = filterCat ? items.filter((i) => i.category === filterCat) : items;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 items-center">
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Category</span>
          {[
            { k: "", label: "All" },
            { k: "granules", label: "Granules" },
            { k: "master_batch", label: "Master Batch" },
            { k: "paper_tube", label: "Paper Tubes" },
            { k: "carton", label: "Cartons" },
            { k: "packaging", label: "Packaging" },
            { k: "other", label: "Other" },
          ].map((c) => (
            <button
              key={c.k || "all"}
              data-testid={`raw-cat-${c.k || "all"}`}
              onClick={() => setFilterCat(c.k)}
              className={`text-[11px] uppercase font-mono px-3 py-1 border ${filterCat === c.k ? "bg-orange-600 text-white border-orange-600" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100"}`}
            >{c.label}</button>
          ))}
        </div>
        {canCreate && (
          <button data-testid="new-raw-btn" onClick={() => { setEditing(null); setShow(true); }} className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm uppercase tracking-wider font-medium flex items-center gap-2"><Plus size={16} /> Add Inward Item</button>
        )}
      </div>
      {filterCat === "paper_tube" ? (
        <PaperTubePanel rms={visible} onReceive={(rm) => setReceive(rm)} showActions={showActions} />
      ) : (
      <div className="bg-white border border-zinc-300 overflow-x-auto" data-testid="raw-table">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-zinc-100 text-zinc-600 uppercase text-[10px] font-mono tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Category</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Item Name</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Specification / Type</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Grade</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Stock</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Unit</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Wt / Piece (kg)</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Reorder</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Status</th>
              {showActions && <th className="border-b border-zinc-300"></th>}
            </tr>
          </thead>
          <tbody className="font-mono">
            {visible.filter((m) => m.category !== "paper_tube" && m.category !== "paper_tube_master").length === 0 ? (
              <tr><td colSpan={10} className="text-center text-zinc-500 py-12 text-sm font-sans">No items in this category. Click Add Inward Item to record raw inward stock.</td></tr>
            ) : visible.filter((m) => m.category !== "paper_tube" && m.category !== "paper_tube_master").map((m) => {
              const low = m.reorder_level > 0 && m.stock <= m.reorder_level;
              const showWeight = ["paper_tube", "carton"].includes(m.category);
              return (
                <tr key={m.id} className="border-b border-zinc-200 hover:bg-zinc-50">
                  <td className="px-3 py-2"><span className="text-[10px] uppercase border border-zinc-300 bg-zinc-100 text-zinc-700 px-2 py-0.5">{CATEGORY_LABEL[m.category] || m.category}</span></td>
                  <td className="px-3 py-2 font-semibold text-zinc-900 font-sans">{m.name}</td>
                  <td className="px-3 py-2 text-xs">{m.specification || "—"}</td>
                  <td className="px-3 py-2 font-sans">{m.grade || "—"}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${Number(m.stock) < 0 ? "text-red-700" : "text-orange-700"}`} data-testid={`raw-stock-${m.id}`}>{Number(m.stock).toFixed(3)}</td>
                  <td className="px-3 py-2 text-right">{m.unit}</td>
                  <td className="px-3 py-2 text-right">{showWeight ? <span className="text-orange-700">{(m.unit_weight_kg || 0).toFixed(3)}</span> : <span className="text-zinc-400">—</span>}</td>
                  <td className="px-3 py-2 text-right">{m.reorder_level}</td>
                  <td className="px-3 py-2">
                    {low ? (
                      <span className="text-[10px] uppercase border border-orange-400 bg-orange-50 text-orange-800 px-2 py-0.5">LOW STOCK</span>
                    ) : (
                      <span className="text-[10px] uppercase border border-zinc-300 bg-zinc-50 text-zinc-700 px-2 py-0.5">OK</span>
                    )}
                  </td>
                  {showActions && (
                    <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                      {canReceive && (
                        <button data-testid={`receive-raw-${m.id}`} onClick={() => setReceive(m)} className="inline-flex items-center gap-1 text-xs uppercase border border-zinc-300 px-2 py-1 hover:bg-zinc-100"><ArrowDownToLine size={12} /> Receive</button>
                      )}
                      {canEditRaw ? (
                        <button data-testid={`edit-raw-${m.id}`} onClick={() => { setEditing(m); setShow(true); }} className="text-zinc-600 hover:text-zinc-900 inline-block"><Pencil size={14} /></button>
                      ) : (
                        <span data-testid={`edit-raw-${m.id}-admin-only`} title="Editing raw material requires admin approval" className="text-zinc-300 cursor-not-allowed inline-block"><Lock size={14} /></span>
                      )}
                      {canDeleteRaw ? (
                        <button data-testid={`delete-raw-${m.id}`} onClick={async () => {
                          if (!window.confirm(`Delete ${m.name}?`)) return;
                          try { await api.delete(`/raw-materials/${m.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
                        }} className="text-red-600 hover:text-red-800 inline-block"><Trash2 size={14} /></button>
                      ) : (
                        <span data-testid={`delete-raw-${m.id}-admin-only`} title="Deleting raw material requires admin approval" className="text-zinc-300 cursor-not-allowed inline-block"><Lock size={14} /></span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {show && <RawForm editing={editing} onClose={() => { setShow(false); setEditing(null); }} onSaved={() => { setShow(false); setEditing(null); load(); }} />}
      {receive && <ReceiveStock material={receive} onClose={() => setReceive(null)} onSaved={() => { setReceive(null); load(); }} />}
    </div>
  );
}

const CATEGORIES = [
  { k: "granules", label: "Granules (LLDPE/LDPE)" },
  { k: "master_batch", label: "Master Batch / Additive" },
  { k: "paper_tube", label: "Paper Tube / Core" },
  { k: "carton", label: "Carton / Box" },
  { k: "packaging", label: "Packaging (Stretch, Tape, Label)" },
  { k: "other", label: "Other" },
];

const CATEGORY_LABEL = CATEGORIES.reduce((a, c) => ({ ...a, [c.k]: c.label.split(" ")[0] }), {});

const UNITS = ["kg", "ton", "bag", "nos", "pcs", "m", "roll"];

function PaperTubePanel({ rms, onReceive, showActions }) {
  const [summary, setSummary] = useState(null);
  const load = () => api.get("/reports/paper-tube-summary").then((r) => setSummary(r.data)).catch(() => toast.error("Could not load"));
  useEffect(() => { load(); }, []);
  // Reload when any RM list changes (covers updates after Edit).
  useEffect(() => { load(); }, [rms.length]);

  if (!summary) return <div className="text-sm font-mono text-zinc-500">Loading paper tube summary…</div>;

  // Master pool RM object (for the Receive button payload).
  const masterRm = rms.find((r) => r.id === summary.master_id) || {
    id: summary.master_id, name: "Paper Tube Master Stock", unit: "kg",
    stock: summary.master_pool_stock_kg, opening_stock: summary.master_received_kg, category: "paper_tube_master",
  };
  const balance = summary.master_pool_stock_kg;

  return (
    <div className="space-y-3" data-testid="paper-tube-panel">
      <div className="bg-white border border-zinc-300 overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-zinc-100 text-zinc-600 uppercase text-[10px] font-mono tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Paper Tube Subtype</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Specification</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Wt / Piece (kg)</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Used (pieces)</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Used (kg)</th>
              {showActions && <th className="border-b border-zinc-300"></th>}
            </tr>
          </thead>
          <tbody className="font-mono">
            {summary.subtypes.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-zinc-500 py-12 text-sm font-sans">No paper tube subtypes defined yet.</td></tr>
            ) : summary.subtypes.map((s) => (
              <tr key={s.id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`paper-tube-row-${s.id}`}>
                <td className="px-3 py-2 font-semibold text-zinc-900 font-sans">{s.name}</td>
                <td className="px-3 py-2 text-xs">{s.specification || "—"}</td>
                <td className="px-3 py-2 text-right text-orange-700">{Number(s.unit_weight_kg).toFixed(3)}</td>
                <td className="px-3 py-2 text-right">{Number(s.used_pieces).toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-semibold">{Number(s.used_kg).toFixed(3)}</td>
                {showActions && <td className="px-3 py-2 text-right text-[10px] text-zinc-400 font-sans">spec only</td>}
              </tr>
            ))}
            <tr className="bg-zinc-50 border-t-2 border-zinc-400 font-semibold">
              <td colSpan={3} className="px-3 py-2 text-right uppercase text-[10px] font-mono tracking-widest">Total Used</td>
              <td className="px-3 py-2 text-right">{Number(summary.total_used_pieces).toFixed(2)}</td>
              <td className="px-3 py-2 text-right text-orange-700">{Number(summary.total_used_kg).toFixed(3)} kg</td>
              {showActions && <td></td>}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="bg-orange-50 border-2 border-orange-400 px-4 py-3 grid grid-cols-1 md:grid-cols-4 gap-3 items-center" data-testid="paper-tube-master">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Master Pool — Received</div>
          <div className="font-mono text-xl font-bold text-zinc-900">{Number(summary.master_received_kg).toFixed(3)} kg</div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Total Used</div>
          <div className="font-mono text-xl font-bold text-zinc-900">{Number(summary.master_used_kg ?? summary.total_used_kg).toFixed(3)} kg</div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Pool Balance (Received − Used)</div>
          <div className={`font-mono text-2xl font-bold ${balance < 0 ? "text-red-700" : "text-orange-800"}`} data-testid="paper-tube-master-balance">{Number(balance).toFixed(3)} kg</div>
        </div>
        {showActions && (
          <button
            data-testid="paper-tube-receive-btn"
            onClick={() => onReceive(masterRm)}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-3 text-sm uppercase tracking-wider font-mono"
            title="Receive paper tube into the shared master pool"
          >Receive into Master Pool</button>
        )}
      </div>

      <div className="text-[10px] font-mono text-zinc-500">
        Paper tubes are pooled across all subtypes. Receive happens only into the master pool. Consumption per subtype is derived from cartons via <code>unit_weight_kg × pieces used</code>.
      </div>
    </div>
  );
}

function RawForm({ editing, onClose, onSaved }) {
  const initial = editing
    ? { ...editing, opening_stock: editing.opening_stock ?? editing.stock ?? 0 }
    : { name: "", category: "granules", specification: "", grade: "", unit: "kg", unit_weight_kg: 0, opening_stock: 0, stock: 0, reorder_level: 0 };
  const [form, setForm] = useState(initial);
  const consumed = editing
    ? Math.max(0, (editing.opening_stock ?? editing.stock ?? 0) - (editing.stock ?? 0))
    : 0;
  const computedCurrent = Math.max(0, (form.opening_stock || 0) - consumed);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // {affected_cartons, skipped_dispatched, products}
  const submit = async (e) => {
    e.preventDefault();
    // If editing AND unit_weight_kg changed, fetch a cascade-impact preview first.
    if (editing && Math.abs((editing.unit_weight_kg || 0) - (form.unit_weight_kg || 0)) > 0.0005) {
      try {
        const { data } = await api.get(`/raw-materials/${editing.id}/recalc-preview`, { params: { unit_weight_kg: form.unit_weight_kg || 0 } });
        if ((data.affected_cartons || 0) > 0 || (data.skipped_dispatched || 0) > 0) {
          setConfirm(data);
          return;
        }
      } catch (err) {
        // Don't silently swallow — let the user know we couldn't compute the impact.
        toast.warning("Could not compute cascade preview — saving without recalculation summary");
      }
    }
    await persist();
  };
  const persist = async () => {
    setBusy(true);
    try {
      if (editing) await api.put(`/raw-materials/${editing.id}`, form);
      else await api.post("/raw-materials", form);
      toast.success(editing ? "Updated" : "Item added");
      setConfirm(null);
      onSaved();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value });
  const cls = "w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono";

  // Smart placeholder based on category
  const SPEC_PLACEHOLDER = {
    granules: "e.g. C8, MFI 1.0, density 0.918",
    master_batch: "e.g. White MB, 5% titanium dioxide",
    paper_tube: "e.g. 3 inch ID 76mm, OD 80mm, length 250mm",
    carton: "e.g. 5-ply, 600 x 400 x 300 mm, 18kg load",
    packaging: "e.g. Brand label, BOPP tape 48mm",
    other: "Describe the item",
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-10" data-testid="raw-form-modal">
      <div className="bg-white border border-zinc-300 w-full max-w-2xl">
        <div className="flex items-center justify-between border-b border-zinc-300 px-6 py-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Inward Inventory</div>
            <h2 className="text-xl font-semibold">{editing ? "Edit Item" : "Add Custom Inward Item"}</h2>
          </div>
          <button onClick={onClose}><X /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block">Category *</label>
              <select data-testid="raw-category" value={form.category} onChange={f("category")} className={cls}>
                {CATEGORIES.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block">Item Name *</label>
              <input data-testid="raw-name" required value={form.name} onChange={f("name")} placeholder={form.category === "paper_tube" ? "3 inch Paper Tube" : form.category === "carton" ? "Export Carton" : "Item name"} className={cls} />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase mb-1 block">Specification / Type</label>
            <input data-testid="raw-spec" value={form.specification} onChange={f("specification")} placeholder={SPEC_PLACEHOLDER[form.category]} className={cls} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block">Grade</label>
              <input data-testid="raw-grade" value={form.grade} onChange={f("grade")} className={cls} />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block">Unit *</label>
              <select data-testid="raw-unit" value={form.unit} onChange={f("unit")} className={cls}>
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block text-zinc-500">Opening Stock</label>
              <input data-testid="raw-opening-stock-locked" disabled value="0 (use Receive to add stock)" className="w-full h-9 px-2 text-sm border border-zinc-200 bg-zinc-100 font-mono text-zinc-500" />
            </div>
          </div>
          {form.category === "paper_tube" ? (
            <div className="bg-orange-50 border border-orange-300 px-3 py-2 text-[11px] text-orange-900 -mt-1">
              Paper tube inventory is tracked as <b>one shared kg pool</b>. This subtype contributes its <b>Unit Weight (kg/piece)</b> below — when a carton consumes N cores of this subtype, the system deducts <b>N × unit weight</b> from the master pool. Opening / per-piece stock does not apply here.
            </div>
          ) : editing && (
            <div className="grid grid-cols-3 gap-3 -mt-2 text-[11px] font-mono">
              <div className="bg-zinc-50 border border-zinc-300 px-3 py-2">
                <div className="text-[9px] uppercase tracking-widest text-zinc-500">Consumed so far</div>
                <div className="text-zinc-900" data-testid="raw-consumed">{consumed.toFixed(3)} {form.unit}</div>
              </div>
              <div className="bg-zinc-50 border border-zinc-300 px-3 py-2">
                <div className="text-[9px] uppercase tracking-widest text-zinc-500">Current stock (auto)</div>
                <div className={`font-semibold ${computedCurrent < 0 ? "text-red-700" : "text-orange-700"}`} data-testid="raw-computed-current">{computedCurrent.toFixed(3)} {form.unit}</div>
              </div>
              <div className="bg-zinc-50 border border-zinc-300 px-3 py-2">
                <div className="text-[9px] uppercase tracking-widest text-zinc-500">Formula</div>
                <div className="text-zinc-700 text-[10px] leading-tight">opening − consumed (use Receive to add stock)</div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block">Unit Weight (kg / piece)</label>
              <input
                data-testid="raw-unitwt"
                type="number"
                step="0.001"
                value={form.unit_weight_kg || 0}
                onChange={f("unit_weight_kg")}
                placeholder={form.category === "carton" ? "e.g. 0.6 (empty carton)" : form.category === "paper_tube" ? "e.g. 0.25" : "0 if N/A"}
                className={cls}
              />
              <div className="text-[10px] text-zinc-500 mt-1">Used by finished-goods category to auto-fill gross weight when this item is selected as core/carton.</div>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block flex items-center gap-2">
                <input
                  data-testid="raw-reorder-enabled"
                  type="checkbox"
                  checked={(parseFloat(form.reorder_level) || 0) > 0}
                  onChange={(e) => setForm({ ...form, reorder_level: e.target.checked ? 1 : 0 })}
                  className="accent-orange-600"
                />
                Enable Low Stock Alert
              </label>
              <input
                data-testid="raw-reorder"
                type="number"
                step="0.01"
                min="0"
                disabled={(parseFloat(form.reorder_level) || 0) === 0}
                value={form.reorder_level}
                onChange={f("reorder_level")}
                className={cls + ((parseFloat(form.reorder_level) || 0) === 0 ? " bg-zinc-100 text-zinc-400" : "")}
              />
              <div className="text-[10px] text-zinc-500 mt-1">Uncheck to disable alerting on this raw material.</div>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase mb-1 block">Default GST % (used on Receive)</label>
            <input
              data-testid="raw-gst-pct"
              type="number"
              step="0.01"
              value={form.gst_pct || 0}
              onChange={f("gst_pct")}
              className={cls}
              placeholder="e.g. 18"
            />
            <div className="text-[10px] text-zinc-500 mt-1">Auto-fills the GST % field every time you click Receive for this item. Editable per receipt.</div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm uppercase border border-zinc-300 bg-white">Cancel</button>
            <button data-testid="save-raw-btn" type="submit" disabled={busy} className="px-5 py-2 text-sm uppercase bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60">{busy ? "Saving..." : editing ? "Update" : "Add Item"}</button>
          </div>
        </form>
      </div>

      {confirm && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" data-testid="rm-cascade-modal">
          <div className="bg-white border-2 border-orange-600 w-full max-w-lg">
            <div className="px-5 py-4 border-b border-zinc-200 bg-orange-50">
              <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Cascade Update</div>
              <div className="text-base font-semibold text-zinc-950 mt-0.5">Recalculate stock weights?</div>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm">
              <p className="text-zinc-700">
                Changing <span className="font-semibold">{editing?.name}</span> unit weight from <span className="font-mono">{editing?.unit_weight_kg}</span> → <span className="font-mono text-orange-700 font-semibold">{form.unit_weight_kg}</span> kg will recompute net weight on:
              </p>
              <div className="border border-zinc-300 px-3 py-2 font-mono text-xs space-y-1">
                <div className="flex justify-between"><span className="text-zinc-600">In-stock cartons affected</span><span className="font-semibold text-orange-700" data-testid="cascade-affected">{confirm.affected_cartons}</span></div>
                <div className="flex justify-between"><span className="text-zinc-600">Dispatched/Delivered (skipped — history preserved)</span><span className="font-semibold text-zinc-500" data-testid="cascade-skipped">{confirm.skipped_dispatched}</span></div>
              </div>
              {(confirm.products || []).length > 0 && (
                <div className="text-[11px] font-mono">
                  <div className="text-zinc-500 uppercase tracking-widest mb-1">Affected categories</div>
                  <ul className="space-y-0.5">
                    {(confirm.products || []).map((pp) => (
                      <li key={pp.id} className="flex justify-between border-b border-dashed border-zinc-200 py-0.5"><span>{pp.sku}</span><span className="text-orange-700 font-semibold">{pp.cartons} ctns</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2 bg-zinc-50">
              <button type="button" onClick={() => setConfirm(null)} className="px-4 py-2 text-xs uppercase tracking-wider font-mono border border-zinc-300 hover:bg-zinc-100" data-testid="cascade-cancel">Cancel</button>
              <button type="button" onClick={persist} disabled={busy} data-testid="cascade-confirm" className="px-4 py-2 text-xs uppercase tracking-wider font-mono bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60">{busy ? "Updating…" : "Confirm & Recalculate"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReceiveStock({ material, onClose, onSaved }) {
  const [qty, setQty] = useState(0);
  const [note, setNote] = useState("");
  const [ppu, setPpu] = useState(0);
  const [gstPct, setGstPct] = useState(material.gst_pct || 0);
  const [supName, setSupName] = useState("");
  const [supGst, setSupGst] = useState("");
  const [supAddr, setSupAddr] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [busy, setBusy] = useState(false);

  // Pre-load supplier directory for autocomplete suggestions.
  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => {});
  }, []);

  // When user picks an existing supplier name, autofill GST/address.
  useEffect(() => {
    if (!supName) return;
    const m = suppliers.find((s) => (s.name || "").toLowerCase() === supName.toLowerCase());
    if (m) {
      if (m.gst && !supGst) setSupGst(m.gst);
      if (m.address && !supAddr) setSupAddr(m.address);
    }
  }, [supName, suppliers, supGst, supAddr]);

  const totalEx = qty * ppu;
  const totalIncl = totalEx * (1 + (gstPct || 0) / 100);

  const submit = async (e) => {
    e.preventDefault();
    if (qty <= 0) { toast.error("Quantity must be > 0"); return; }
    setBusy(true);
    try {
      await api.post(`/raw-materials/${material.id}/receive`, {
        quantity: qty,
        note,
        price_per_unit: ppu,
        gst_pct: gstPct,
        total_price: totalIncl,
        supplier_name: supName.trim(),
        supplier_gst: supGst.trim(),
        supplier_address: supAddr.trim(),
      });
      toast.success(`+${qty} ${material.unit} received as a new lot`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const isPaperTubeSubtype = material.category === "paper_tube";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-6" data-testid="receive-modal">
      <div className="bg-white border border-zinc-300 w-full max-w-lg mx-3">
        <div className="flex items-center justify-between border-b border-zinc-300 px-5 py-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Goods Inward · New Lot</div>
            <h2 className="text-base font-semibold">Receive {material.name}</h2>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3 text-sm">
          {isPaperTubeSubtype && (
            <div className="bg-orange-50 border border-orange-300 px-3 py-2 text-[11px] text-orange-900">
              Paper tube inventory is tracked in <b>kg, pooled across all subtypes</b>. This receive will be added to the shared <b>Paper Tube Master</b> pool. Subtype unit weights are used to compute per-carton kg deduction.
            </div>
          )}
          <div className="bg-zinc-100 border border-zinc-300 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Current Stock</div>
            <div className={`font-mono text-2xl ${Number(material.stock) < 0 ? "text-red-700" : "text-zinc-900"}`}>{Number(material.stock).toFixed(3)} <span className="text-sm text-zinc-500">{material.unit}</span></div>
            {Number(material.stock) < 0 && <div className="text-[10px] text-red-700 font-mono mt-0.5">OVERDRAWN — receive to bring back to positive</div>}
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block">Quantity ({material.unit}) *</label>
              <input data-testid="receive-qty" type="number" step="0.001" required value={qty} onChange={(e) => setQty(parseFloat(e.target.value) || 0)} className="w-full h-9 px-2 text-sm border border-zinc-300 font-mono" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block">Price / {material.unit} (₹)</label>
              <input data-testid="receive-ppu" type="number" step="0.01" value={ppu} onChange={(e) => setPpu(parseFloat(e.target.value) || 0)} className="w-full h-9 px-2 text-sm border border-zinc-300 font-mono" placeholder="ex-GST" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block">GST %</label>
              <input data-testid="receive-gst" type="number" step="0.01" value={gstPct} onChange={(e) => setGstPct(parseFloat(e.target.value) || 0)} className="w-full h-9 px-2 text-sm border border-zinc-300 font-mono" />
            </div>
          </div>

          <div className="bg-zinc-50 border border-zinc-300 p-2 text-[11px] font-mono flex items-center justify-between" data-testid="receive-totals">
            <span className="text-zinc-500 uppercase tracking-widest">Total (incl. GST)</span>
            <span className="text-zinc-900 font-bold text-base">₹ {totalIncl ? totalIncl.toFixed(2) : "0.00"}</span>
          </div>

          <div className="border-t border-zinc-200 pt-3">
            <div className="text-[10px] font-semibold uppercase mb-2 tracking-widest text-zinc-500">Supplier</div>
            <input
              data-testid="receive-supplier-name"
              list="receive-supplier-list"
              value={supName}
              onChange={(e) => setSupName(e.target.value)}
              placeholder="Supplier name (existing or new)"
              className="w-full h-9 px-2 text-sm border border-zinc-300 font-mono mb-2"
            />
            <datalist id="receive-supplier-list">
              {suppliers.map((s) => <option key={s.id} value={s.name} />)}
            </datalist>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="receive-supplier-gst" value={supGst} onChange={(e) => setSupGst(e.target.value)} placeholder="GSTIN" className="h-9 px-2 text-sm border border-zinc-300 font-mono" />
              <input data-testid="receive-supplier-address" value={supAddr} onChange={(e) => setSupAddr(e.target.value)} placeholder="Address" className="h-9 px-2 text-sm border border-zinc-300 font-mono" />
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">If the supplier name is new, it will be added to the Suppliers master.</div>
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase mb-1 block">Note (PO #, invoice #, etc)</label>
            <input data-testid="receive-note" value={note} onChange={(e) => setNote(e.target.value)} className="w-full h-9 px-2 text-sm border border-zinc-300 font-mono" />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase border border-zinc-300 bg-white">Cancel</button>
            <button data-testid="confirm-receive-btn" type="submit" disabled={busy} className="px-5 py-2 text-xs uppercase bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60">{busy ? "Saving lot…" : "Save Lot"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
